import * as React from 'react'
import { ReactElement, useState } from 'react'
import {
    Button,
    ButtonProps,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
} from '@fluentui/react-components'
import { bundleIcon, CheckmarkFilled, CheckmarkRegular, DismissFilled, DismissRegular } from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'

type Props = {
    open?: boolean
    className?: string // The classname is required to pass the theme style.
    onConfirm?: (setOpen: (open: boolean) => void, setSubmitting: (submitting: boolean) => void) => void
    setOpen?: (open?: boolean) => void
    title?: ReactElement | string
    content: ReactElement | string
    triggerButtonProps?: ButtonProps
    confirmButtonProps?: ButtonProps
}

const CheckmarkIcon = bundleIcon(CheckmarkFilled, CheckmarkRegular)
const DismissIcon = bundleIcon(DismissFilled, DismissRegular)

export default function AlertDialog(props: Props): ReactElement {
    const [internalOpen, setInternalOpen] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const { t: translate } = useTranslation()

    const open = props.open !== undefined ? props.open : internalOpen
    const setOpen = props.setOpen || setInternalOpen

    return (
        <Dialog {...props} open={open} onOpenChange={(_event, data) => setOpen(data.open)} modalType="alert">
            {props.triggerButtonProps && (
                <DialogTrigger disableButtonEnhancement>
                    <Button {...props.triggerButtonProps} />
                </DialogTrigger>
            )}
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>{props.title || translate('base.alert.title')}</DialogTitle>
                    <DialogContent>{props.content}</DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance="transparent" icon={<DismissIcon />}>
                                {translate('base.alert.cancel')}
                            </Button>
                        </DialogTrigger>
                        <DialogTrigger disableButtonEnhancement>
                            <Button
                                appearance="primary"
                                disabled={submitting}
                                icon={<CheckmarkIcon />}
                                {...props.confirmButtonProps}
                                onClick={() => {
                                    setSubmitting(true)
                                    props.onConfirm(setOpen, setSubmitting)
                                }}
                            >
                                {props.confirmButtonProps?.children || translate('base.alert.submit')}
                            </Button>
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    )
}
